import {
  Avatar,
  Box,
  Button,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useRef } from "react";
import { HamburgerIcon, SearchIcon } from "@chakra-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import jwt_decode from "jwt-decode";
import axios from "axios";
import { config } from "../config";
import { Reports43101Filters } from "./Reports43101Filters";
import { searchReports43101, setSearchTerm } from "../store/reports43101";
import { Link as RouterLink } from "react-router-dom";

function FilterDrawer() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef();

  return (
    <>
      <Button
        ref={btnRef}
        onClick={onOpen}
        variant="ghost"
        leftIcon={<HamburgerIcon color="amber.300" />}
        _hover={{ bg: "slate.700" }}
        color="amber.200"
        fontWeight="medium"
      >
        Filters
      </Button>
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        finalFocusRef={btnRef}
        size="sm"
      >
        <DrawerOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <DrawerContent bg="slate.800" overflow="auto">
          <DrawerCloseButton color="white" />
          <DrawerHeader borderBottomWidth="1px" borderColor="slate.700">
            <Text
              bgGradient="linear(to-r, amber.300, amber.500)"
              bgClip="text"
              fontWeight="bold"
            >
              Filter Reports
            </Text>
          </DrawerHeader>
          <Box p={4}>
            <Reports43101Filters />
          </Box>
        </DrawerContent>
      </Drawer>
    </>
  );
}

const UserProfileMenu = () => {
  const idCookie = Cookies.get("__needle-id-token");
  let decodedToken = {};
  try {
    decodedToken = jwt_decode(idCookie);
  } catch (e) {
    console.error(e);
  }

  const reqLogout = () => {
    axios({
      method: "GET",
      url: config.LOGOUT_URL,
      withCredentials: true,
    })
      .then(() => {
        window.location.href = `${config.COGNITO_AUTH_BASE_URL}${config.COGNITO_AUTH_LOGOUT_ENDPOINT}?client_id=${config.COGNITO_CLIENT_ID}&logout_uri=${config.COGNITO_LOGOUT_CALLBACK}`;
      })
      .catch((e) => console.log(e));
  };

  return (
    <Box>
      <Menu>
        <MenuButton>
          <Avatar
            size="sm"
            name={decodedToken["cognito:username"]}
            bg="amber.500"
            color="white"
          />
        </MenuButton>
        <MenuList bg="slate.800" borderColor="slate.700">
          <MenuItem
            bg="slate.800"
            color="red.400"
            _hover={{ bg: "slate.700" }}
            onClick={() => reqLogout()}
          >
            Logout
          </MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
};

const SearchBar = () => {
  const dispatch = useDispatch();
  const searchTerm = useSelector((s) => s.reports43101Slice.searchTerm);
  const loadingResults = useSelector((s) => s.reports43101Slice.loadingResults);

  const handleSearch = () => {
    dispatch(searchReports43101());
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <HStack spacing={4} flex={1}>
      <InputGroup>
        <InputLeftElement pointerEvents="none" h="50px" w="50px">
          <SearchIcon color="slate.400" boxSize={5} />
        </InputLeftElement>
        <Input
          value={searchTerm}
          onChange={(e) => dispatch(setSearchTerm(e.target.value))}
          onKeyPress={handleKeyPress}
          placeholder="Search report content, project names, commodities..."
          bg="slate.700"
          border="1px solid"
          borderColor="slate.600"
          color="white"
          fontSize="md"
          h="50px"
          pl="50px"
          _placeholder={{ color: "slate.400" }}
          _focus={{
            borderColor: "amber.400",
            boxShadow: "0 0 0 1px var(--chakra-colors-amber-400)",
          }}
          borderRadius="xl"
        />
      </InputGroup>
      <Button
        onClick={handleSearch}
        isLoading={loadingResults}
        colorScheme="amber"
        h="50px"
        px={8}
        fontSize="md"
        borderRadius="xl"
        fontWeight="semibold"
        _hover={{ transform: "translateY(-1px)", shadow: "lg" }}
        transition="all 0.2s"
      >
        Search
      </Button>
    </HStack>
  );
};

const NavLink = ({ to, children, isActive }) => (
  <Button
    as={RouterLink}
    to={to}
    variant="ghost"
    size="sm"
    color={isActive ? "amber.300" : "slate.300"}
    fontWeight={isActive ? "semibold" : "normal"}
    _hover={{ bg: "slate.700", color: "amber.300" }}
    borderBottom={isActive ? "2px solid" : "none"}
    borderColor="amber.400"
    borderRadius="none"
    px={4}
    py={5}
  >
    {children}
  </Button>
);

export const Reports43101Navbar = () => {
  return (
    <Box
      bg="linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
      position="sticky"
      top={0}
      zIndex={100}
      shadow="lg"
    >
      <Flex maxW="1800px" mx="auto" alignItems="center" gap={4} py={3} px={4}>
        {/* Logo/Brand */}
        <HStack spacing={3}>
          <Text
            fontSize="xl"
            fontWeight="black"
            bgGradient="linear(to-r, amber.300, amber.500)"
            bgClip="text"
            letterSpacing="tight"
          >
            NI 43-101
          </Text>
          <Text
            fontSize="sm"
            color="slate.400"
            display={{ base: "none", md: "block" }}
          >
            Reports Search
          </Text>
        </HStack>

        {/* Navigation Links */}
        <HStack
          spacing={1}
          ml={6}
          display={{ base: "none", lg: "flex" }}
          flexShrink={0}
        >
          <NavLink to="/" isActive={false}>
            News Search
          </NavLink>
          <NavLink to="/reports" isActive={true}>
            43-101 Reports
          </NavLink>
        </HStack>

        {/* Search Bar */}
        <Box flex={1} mx={6}>
          <SearchBar />
        </Box>

        {/* Right side controls */}
        <HStack spacing={3} flexShrink={0}>
          <FilterDrawer />
          <UserProfileMenu />
        </HStack>
      </Flex>
    </Box>
  );
};
